import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  CoreLink,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormTextField,
  Button,
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';
import { ROUTE_ACCOUNT_LOGIN, ROUTE_CUSTOMER_SERVICE } from '../../../config/routes';
import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';
import { DEFAULT_LANGUAGE } from '../../../config/constants';
import { checkEmailExists } from '../../../store/actions/CheckEmail';

const CheckUser = () => {
  const PAGE_TITLE = 'Check user | VeiligheidNL';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const userParamValues = Object.fromEntries(urlParams);
  const userParamEmail = userParamValues.email;

  const checkEmailStatus = useSelector((state) => state.getIn(['ui', 'operationStatus', 'checkEmail', 'fetch', 'status']));

  const validationSchema = Yup.object({
    usernameLogin: Yup.string()
      .email(t('form.register.email.invalid'))
      .required(t('form.register.email.required')),
  });

  const onSubmit = async (values, form) => {
    setErrorMessage('');

    try {
      const result = await dispatch(checkEmailExists(values.usernameLogin));

      if (result.exists) {
        // User exists, redirect to login page with destination parameter
        history.push(`${ROUTE_ACCOUNT_LOGIN}?destination=/cart/checkoutv2&email=${encodeURIComponent(values.usernameLogin)}`);
      } else {
        // User doesn't exist, email sent, redirect to confirmation page
        history.push('/success/email-sent');
      }
    } catch (error) {
      setErrorMessage(t('check_user.error_general'));
    }
  };

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <Container small>
        <div className="FormPage PageLogin PageMinimal" id="main">
          <div className="PageMinimal__top">
            <CoreLink className="Logo" href="/">
              <img src={LogoImageVNL} alt="VeiligheidNL" />
            </CoreLink>
            <h1>{t('check_user.title')}</h1>
          </div>
          <div className="PageMinimal__content">
            {errorMessage && (
              <div className="Form__error">
                <p>{errorMessage}</p>
              </div>
            )}
            <FormikWrapper
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              key={`check-user-form-${userParamEmail || 'default'}`}
              initialValues={{
                usernameLogin: userParamEmail || '',
              }}
            >
              <div className="PageLogin__username">
                <FormTextField
                  title={t('check_user.email.label')}
                  type="email"
                  fieldName="usernameLogin"
                  required
                  disabled={userParamEmail}
                  autocomplete={userParamEmail ? 'off' : 'on'}
                />
              </div>

              <div className="Form__actions">
                <Button
                  type="submit"
                  className={`Button Button--primary ${checkEmailStatus === UI_OPERATION_STATUS_REQUEST ? 'Button--loading' : ''}`}
                >
                  {t('check_user.submit')}
                  <i className="icon icon-arrow-right" />
                </Button>
              </div>
            </FormikWrapper>
          </div>
          <div className="PageMinimal__usp">
            <div className="PageMinimal__usp__link">
              <CoreLink href={ROUTE_CUSTOMER_SERVICE} target="_blank" className="Button Button--secondary Button--help">
                {t('general.need_help')}
              </CoreLink>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default CheckUser;
